import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for intelligent caching with TTL and LRU eviction
 * @param {string} cacheKey - Unique identifier for the cache
 * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
 * @param {number} maxSize - Maximum number of items in cache (default: 100)
 * @returns {Object} - Cache utilities
 */
export function useCache(cacheKey = 'default', ttl = 5 * 60 * 1000, maxSize = 100) {
  const cacheRef = useRef(new Map());
  const accessOrderRef = useRef(new Map()); // For LRU tracking
  
  const isExpired = useCallback((item) => {
    return Date.now() - item.timestamp > ttl;
  }, [ttl]);

  const evictLRU = useCallback(() => {
    if (cacheRef.current.size >= maxSize) {
      // Find oldest accessed item
      let oldestKey = null;
      let oldestAccess = Date.now();
      
      for (const [key, lastAccess] of accessOrderRef.current) {
        if (lastAccess < oldestAccess) {
          oldestAccess = lastAccess;
          oldestKey = key;
        }
      }
      
      if (oldestKey) {
        cacheRef.current.delete(oldestKey);
        accessOrderRef.current.delete(oldestKey);
      }
    }
  }, [maxSize]);

  const set = useCallback((key, data) => {
    const fullKey = `${cacheKey}:${key}`;
    
    // Evict expired items first
    for (const [cachedKey, item] of cacheRef.current) {
      if (isExpired(item)) {
        cacheRef.current.delete(cachedKey);
        accessOrderRef.current.delete(cachedKey);
      }
    }
    
    // Evict LRU if needed
    evictLRU();
    
    // Add new item
    cacheRef.current.set(fullKey, {
      data,
      timestamp: Date.now()
    });
    
    accessOrderRef.current.set(fullKey, Date.now());
  }, [cacheKey, isExpired, evictLRU]);

  const get = useCallback((key) => {
    const fullKey = `${cacheKey}:${key}`;
    const item = cacheRef.current.get(fullKey);
    
    if (!item || isExpired(item)) {
      cacheRef.current.delete(fullKey);
      accessOrderRef.current.delete(fullKey);
      return null;
    }
    
    // Update access time for LRU
    accessOrderRef.current.set(fullKey, Date.now());
    return item.data;
  }, [cacheKey, isExpired]);

  const remove = useCallback((key) => {
    const fullKey = `${cacheKey}:${key}`;
    cacheRef.current.delete(fullKey);
    accessOrderRef.current.delete(fullKey);
  }, [cacheKey]);

  const clear = useCallback(() => {
    cacheRef.current.clear();
    accessOrderRef.current.clear();
  }, []);

  const has = useCallback((key) => {
    const fullKey = `${cacheKey}:${key}`;
    const item = cacheRef.current.get(fullKey);
    return item && !isExpired(item);
  }, [cacheKey, isExpired]);

  const size = useCallback(() => {
    return cacheRef.current.size;
  }, []);

  const getStats = useCallback(() => {
    let expired = 0;
    const now = Date.now();
    
    for (const [, item] of cacheRef.current) {
      if (isExpired(item)) expired++;
    }
    
    return {
      total: cacheRef.current.size,
      expired,
      valid: cacheRef.current.size - expired,
      ttl,
      maxSize,
      utilization: (cacheRef.current.size / maxSize) * 100
    };
  }, [isExpired, ttl, maxSize]);

  return {
    set,
    get,
    remove,
    clear,
    has,
    size,
    getStats
  };
}

/**
 * Advanced cache hook with automatic API integration
 * @param {Function} fetchFunction - Function to fetch data when not cached
 * @param {Object} options - Cache options
 * @returns {Object} - Cached data and utilities
 */
export function useCachedAPI(fetchFunction, options = {}) {
  const {
    cacheKey = 'api',
    ttl = 5 * 60 * 1000, // 5 minutes
    maxSize = 50,
    dependencies = [],
    staleWhileRevalidate = false
  } = options;

  const cache = useCache(cacheKey, ttl, maxSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWithCache = useCallback(async (...args) => {
    const cacheKey = JSON.stringify(args);
    
    // Try to get from cache first
    const cached = cache.get(cacheKey);
    if (cached && !staleWhileRevalidate) {
      return cached;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchFunction(...args);
      
      // Cache the result
      cache.set(cacheKey, data);
      
      return data;
    } catch (err) {
      setError(err);
      
      // Return stale data if available and staleWhileRevalidate is enabled
      if (cached && staleWhileRevalidate) {
        return cached;
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, cache, staleWhileRevalidate]);

  // Clear cache when dependencies change
  useEffect(() => {
    cache.clear();
  }, dependencies);

  return {
    fetchWithCache,
    loading,
    error,
    cache,
    clearCache: cache.clear,
    getCacheStats: cache.getStats
  };
}

/**
 * Hook for browser localStorage caching with compression
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @param {Object} options - Options
 * @returns {Array} - [value, setValue, clearValue]
 */
export function useLocalStorageCache(key, defaultValue = null, options = {}) {
  const {
    ttl = 24 * 60 * 60 * 1000, // 24 hours
    compress = false,
    serialize = JSON.stringify,
    deserialize = JSON.parse
  } = options;

  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) return defaultValue;

      const parsed = deserialize(item);
      
      // Check if expired
      if (parsed.timestamp && Date.now() - parsed.timestamp > ttl) {
        window.localStorage.removeItem(key);
        return defaultValue;
      }

      return parsed.data ?? defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const dataToStore = {
        data: value,
        timestamp: Date.now()
      };

      window.localStorage.setItem(key, serialize(dataToStore));
      setStoredValue(value);
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, serialize]);

  const clearValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(defaultValue);
    } catch (error) {
      console.error(`Error clearing localStorage key "${key}":`, error);
    }
  }, [key, defaultValue]);

  const isExpired = useCallback(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) return true;

      const parsed = deserialize(item);
      return Date.now() - parsed.timestamp > ttl;
    } catch {
      return true;
    }
  }, [key, ttl, deserialize]);

  return [storedValue, setValue, clearValue, isExpired];
}

export default useCache;